/**
 * Cross-feature notifications service.
 *
 * The single SSOT for the universal NotificationsBell. Both mentorship
 * and networking dispatch through the backend's api.notifications
 * subpackage, so the frontend has one API surface here that handles
 * every notification type via the registry-driven shape:
 *
 *   { id, type, payload, read_at, created_at,
 *     display, category, icon, default_url }
 *
 * The four enriched fields (display/category/icon/default_url) are
 * server-rendered by the backend's api.notifications.registry, so the
 * bell can render any notification type — current or future — with no
 * frontend code changes. This is the Open/Closed principle applied
 * across the SSOT layer.
 */

import { apiClient } from './apiClient';
import { apiUrl as url } from './apiUrls';

// ────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────

/** Stable, namespaced category identifier — matches NotificationType.category */
export type NotificationCategory =
    | 'social'
    | 'message'
    | 'system'
    | 'review'
    | 'general';

/** A notification as the API returns it, with registry enrichment baked in. */
export type Notification = {
    id: string;
    type: string;
    payload: Record<string, unknown>;
    read_at: string | null;
    created_at: string;
    /** Server-rendered human-readable line. The bell renders this directly. */
    display: string;
    /** Bucket the notification belongs to (drives badge colour + summary). */
    category: NotificationCategory;
    /** Lucide icon name. */
    icon: string;
    /** Frontend route to navigate on click. Empty string = no navigation. */
    default_url: string;
};

/** Bell badge summary — single round-trip the bell badge needs. */
export type UnreadSummary = {
    total: number;
    by_category: Partial<Record<string, number>>;
};

export type PagedResponse<T> = {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
};

// ────────────────────────────────────────────────────────────────────────
// Read
// ────────────────────────────────────────────────────────────────────────

/**
 * List notifications for the current user. Returns the paginated envelope
 * so the bell can show the first page and lazily fetch more if needed.
 */
export async function listNotifications(
    filters: {
        unread_only?: boolean;
        category?: string;
        type?: string;
        page?: number;
    } = {},
): Promise<PagedResponse<Notification>> {
    const params = new URLSearchParams();
    if (filters.unread_only) params.set('unread_only', '1');
    if (filters.category) params.set('category', filters.category);
    if (filters.type) params.set('type', filters.type);
    if (filters.page) params.set('page', String(filters.page));
    const qs = params.toString();
    const response = await apiClient.get(
        url(`/api/notifications/${qs ? '?' + qs : ''}`),
    );
    return response.data;
}

/**
 * One round-trip the bell badge needs: total count + per-category breakdown.
 * Cheaper than fetching the full list when all you want is the badge.
 */
export async function getUnreadSummary(): Promise<UnreadSummary> {
    const response = await apiClient.get(url('/api/notifications/unread_summary/'));
    return response.data;
}

// ────────────────────────────────────────────────────────────────────────
// Write
// ────────────────────────────────────────────────────────────────────────

export async function markNotificationRead(notificationId: string): Promise<Notification> {
    const response = await apiClient.post(
        url(`/api/notifications/${notificationId}/read/`),
    );
    return response.data;
}

export async function markAllNotificationsRead(): Promise<{ marked: number }> {
    const response = await apiClient.post(url('/api/notifications/read_all/'));
    return response.data;
}

export async function markManyNotificationsRead(ids: string[]): Promise<{ marked: number }> {
    const response = await apiClient.post(
        url('/api/notifications/read_many/'),
        { ids },
    );
    return response.data;
}
